import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaTrophy,
  FaBuilding,
  FaCode,
  FaServer,
  FaMobile,
  FaBug,
  FaCloud,
  FaCalculator,
  FaCheck,
  FaStar,
  FaQuoteLeft,
  FaUsers,
  FaChartLine,
  FaClock,
  FaRocket,
  FaPhone,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';

const CaseStudies = () => {
  const getRoleIcon = (roles) => {
    if (roles.includes('Frontend')) return <FaCode />;
    if (roles.includes('Backend')) return <FaServer />;
    if (roles.includes('Mobile')) return <FaMobile />;
    if (roles.includes('QA')) return <FaBug />;
    if (roles.includes('DevOps')) return <FaCloud />;
    return <FaCode />;
  };
  const caseStudies = [
    {
      type: 'SaaS Startup',
      roles: 'Full Stack Developer (2), QA Engineer (1)',
      duration: '3 Months',
      outcome: 'Faster releases and reduced hiring costs',
      description: 'A growing SaaS company needed to accelerate their product development cycle. We provided two full-stack developers and one QA engineer who integrated seamlessly with their existing team.',
      challenge: 'The company was struggling with slow release cycles and couldn\'t find qualified developers quickly enough to meet their growth targets.',
      solution: 'We provided pre-vetted full-stack developers and QA engineers who were onboarded within 48 hours. They integrated with the existing team using React, Node.js, and automated testing frameworks.',
      technologies: 'React, Node.js, PostgreSQL, Jest, Cypress',
      metrics: ['40% faster release cycles', '60% reduction in hiring time', 'Zero onboarding delays', '100% code quality maintained']
    },
    {
      type: 'FinTech Company',
      roles: 'Backend Developer (3), DevOps Engineer (1)',
      duration: '6 Months',
      outcome: 'Scalable infrastructure and improved security',
      description: 'A financial technology startup required robust backend systems to handle increasing transaction volumes. Our team delivered secure, scalable APIs and cloud infrastructure.',
      challenge: 'The company needed to scale their backend infrastructure to handle 10x transaction volume while maintaining security compliance and 99.9% uptime.',
      solution: 'Our backend and DevOps engineers implemented microservices architecture, automated CI/CD pipelines, and enhanced security measures. They worked with AWS, Docker, and Kubernetes to ensure scalability.',
      technologies: 'Python, Node.js, AWS, Docker, Kubernetes, PostgreSQL',
      metrics: ['99.9% uptime achieved', '50% faster API response times', 'Enhanced security compliance', '10x transaction capacity']
    },
    {
      type: 'E-commerce Platform',
      roles: 'Frontend Developer (2), Mobile Developer (1)',
      duration: '4 Months',
      outcome: 'Enhanced user experience and mobile growth',
      description: 'An e-commerce platform wanted to improve their user interface and launch a mobile app. We delivered a modern, responsive design and native mobile application.',
      challenge: 'The platform had an outdated UI and no mobile presence, resulting in declining user engagement and lost mobile market opportunities.',
      solution: 'Our frontend and mobile developers redesigned the web interface with React and built native iOS and Android apps. They focused on performance optimization and user experience.',
      technologies: 'React, Next.js, React Native, TypeScript, Redux',
      metrics: ['35% increase in conversion rate', '200% mobile user growth', 'Improved user satisfaction scores', '50% faster page load times']
    },
    {
      type: 'Healthcare Startup',
      roles: 'Full Stack Developer (1), QA Engineer (2)',
      duration: '8 Months',
      outcome: 'HIPAA-compliant product delivery',
      description: 'A healthcare technology company needed developers familiar with healthcare regulations. We ensured compliance while delivering a patient management system.',
      challenge: 'The company needed to build a HIPAA-compliant patient management system with strict security requirements and regulatory compliance.',
      solution: 'Our developers with healthcare domain expertise implemented secure data handling, encryption, audit trails, and compliance features. QA engineers ensured all regulatory requirements were met.',
      technologies: 'Python, Django, PostgreSQL, AWS, HIPAA-compliant infrastructure',
      metrics: ['100% HIPAA compliance', 'Zero security incidents', 'On-time regulatory submissions', '99.5% system reliability']
    },
    {
      type: 'Enterprise Software',
      roles: 'Backend Developer (4), DevOps Engineer (2)',
      duration: '12 Months',
      outcome: 'Enterprise-grade scalability and performance',
      description: 'A large enterprise required a complete backend overhaul. Our team modernized legacy systems and implemented cloud-native architecture.',
      challenge: 'The enterprise had legacy monolithic systems that couldn\'t scale and were expensive to maintain. They needed a complete modernization without disrupting operations.',
      solution: 'Our team implemented a phased migration strategy, breaking down monoliths into microservices, moving to cloud infrastructure, and implementing modern DevOps practices.',
      technologies: 'Java, Spring Boot, .NET Core, Azure, Kubernetes, Terraform',
      metrics: ['300% performance improvement', '90% reduction in infrastructure costs', 'Seamless migration completion', 'Zero downtime during migration']
    },
    {
      type: 'EdTech Platform',
      roles: 'Frontend Developer (3), Mobile Developer (2)',
      duration: '5 Months',
      outcome: 'Cross-platform learning experience',
      description: 'An education technology company wanted to create an engaging learning platform. We built responsive web and mobile applications with interactive features.',
      challenge: 'The company needed a unified learning experience across web and mobile platforms with interactive features, real-time collaboration, and offline capabilities.',
      solution: 'Our team built a responsive web application and native mobile apps with features like video streaming, interactive quizzes, real-time chat, and offline content sync.',
      technologies: 'React, Vue.js, Flutter, WebRTC, Firebase, MongoDB',
      metrics: ['150% increase in user engagement', 'Cross-platform consistency', 'Improved learning outcomes', '80% user retention rate']
    }
  ];

  const overallStats = [
    {
      icon: <FaUsers />,
      number: '500+',
      label: 'Engineers Deployed',
      description: 'Successfully placed across various industries'
    },
    {
      icon: <FaChartLine />,
      number: '98%',
      label: 'Client Satisfaction',
      description: 'Average client rating across all projects'
    },
    {
      icon: <FaClock />,
      number: '24-72hrs',
      label: 'Average Onboarding',
      description: 'Time to get developers ready to work'
    },
    {
      icon: <FaTrophy />,
      number: '200+',
      label: 'Projects Completed',
      description: 'Successful staff augmentation engagements'
    }
  ];

  const testimonials = [
    {
      quote: 'The Code Icon provided us with exceptional developers who integrated seamlessly with our team. We were able to scale our product development 3x faster than we could have with traditional hiring.',
      author: 'Sarah Johnson',
      role: 'CTO, SaaS Startup',
      rating: 5
    },
    {
      quote: 'Working with The Code Icon transformed our engineering capabilities. Their developers are not just contractors - they\'re true team members who care about our product\'s success.',
      author: 'Michael Chen',
      role: 'Engineering Lead, FinTech Company',
      rating: 5
    },
    {
      quote: 'The speed at which we got developers onboarded and productive was incredible. Within a week, they were contributing meaningful code to our codebase.',
      author: 'Emily Rodriguez',
      role: 'Product Manager, E-commerce Platform',
      rating: 5
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '120px', backgroundColor: 'var(--bg-grey-light)', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
          zIndex: 1
        }}></div>
        <div className="container text-center" style={{ position: 'relative', zIndex: 2 }}>
          <FaTrophy style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }} />
          <h1 className="h1" style={{ marginBottom: '24px' }}>
            Teams We've Helped Scale
          </h1>
          <p className="text-lg mb-6" style={{ maxWidth: '700px', margin: '0 auto 32px', fontSize: '20px', color: 'var(--text-medium)' }}>
            Real success stories from companies that scaled their engineering teams with our staff augmentation model. 
            See how we've helped startups and enterprises achieve their goals.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/calculator" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Get Team Cost
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPhone /> Schedule a Call
            </Link>
          </div>
        </div>
      </section>

      {/* Overall Stats Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Our Track Record</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Numbers that speak for themselves
            </p>
          </div>
          <div className="grid grid-2">
            {overallStats.map((stat, index) => (
              <div key={index} className="card" style={{ padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }}>
                  {stat.icon}
                </div>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--primary-green)', marginBottom: '8px' }}>
                  {stat.number}
                </div>
                <h3 className="h4 mb-2" style={{ color: 'var(--text-dark)' }}>{stat.label}</h3>
                <p style={{ color: 'var(--text-medium)', fontSize: '14px' }}>{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section section-grey">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Success Stories</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Detailed case studies from companies across different industries
            </p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {caseStudies.map((study, index) => (
              <div key={index} className="card" style={{ padding: '32px', marginBottom: '32px' }}>
                <div style={{
                  backgroundColor: 'var(--primary-green)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: 'var(--border-radius)',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <FaBuilding style={{ fontSize: '28px' }} />
                  <h3 className="h3" style={{ margin: 0, color: 'white' }}>{study.type}</h3>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '20px', color: 'var(--primary-green)' }}>
                      {getRoleIcon(study.roles)}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--text-medium)', fontWeight: '600' }}>Team Roles</div>
                      <div style={{ fontSize: '15px', color: 'var(--text-dark)', fontWeight: '600' }}>{study.roles}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <FaClock style={{ color: 'var(--primary-green)', fontSize: '18px' }} />
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--text-medium)', fontWeight: '600' }}>Duration</div>
                      <div style={{ fontSize: '15px', color: 'var(--text-dark)', fontWeight: '600' }}>{study.duration}</div>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'var(--bg-grey-light)',
                  padding: '16px',
                  borderRadius: 'var(--border-radius)',
                  marginBottom: '20px'
                }}>
                  <h4 className="h4" style={{ marginBottom: '8px', color: 'var(--text-dark)', fontSize: '16px' }}>Challenge</h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-medium)', lineHeight: '1.6', marginBottom: '12px' }}>
                    {study.challenge}
                  </p>
                  <h4 className="h4" style={{ marginBottom: '8px', color: 'var(--text-dark)', fontSize: '16px' }}>Solution</h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-medium)', lineHeight: '1.6' }}>
                    {study.solution}
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary-green)', marginBottom: '8px' }}>
                    Technologies Used:
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                    {study.technologies}
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'var(--bg-grey-light)',
                  padding: '16px',
                  borderRadius: 'var(--border-radius)',
                  marginBottom: '20px'
                }}>
                  <h4 className="h4" style={{ marginBottom: '12px', color: 'var(--primary-green)', fontSize: '16px' }}>
                    Key Results:
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {study.metrics.map((metric, idx) => (
                      <li key={idx} style={{
                        padding: '8px 0',
                        borderBottom: idx < study.metrics.length - 1 ? '1px solid var(--border-grey)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <FaCheckCircle style={{ color: 'var(--primary-green)', fontSize: '16px', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>{metric}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--primary-green)',
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: 'var(--border-radius)',
                  border: '2px solid var(--primary-green)'
                }}>
                  {study.outcome}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">What Our Clients Say</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Real feedback from companies we've worked with
            </p>
          </div>
          <div className="grid grid-3">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card" style={{ padding: '32px' }}>
                <div style={{ marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} style={{ color: '#FFD700', fontSize: '18px', marginRight: '4px' }} />
                  ))}
                </div>
                <FaQuoteLeft style={{ fontSize: '32px', color: 'var(--primary-green)', marginBottom: '16px', opacity: 0.3 }} />
                <p style={{ 
                  fontSize: '16px', 
                  color: 'var(--text-dark)', 
                  lineHeight: '1.6', 
                  marginBottom: '20px',
                  fontStyle: 'italic'
                }}>
                  "{testimonial.quote}"
                </p>
                <div style={{
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border-grey)'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '4px' }}>
                    {testimonial.author}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-medium)' }}>
                    {testimonial.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-grey">
        <div className="container text-center">
          <FaRocket style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }} />
          <h2 className="h2 mb-6">Ready to scale your engineering team?</h2>
          <p className="text-lg mb-8" style={{ maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            Join these successful companies and build your dream engineering team with our staff augmentation services. 
            Get started today and see results within 24-72 hours.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPhone /> Schedule a Call
            </Link>
            <Link to="/calculator" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Get Team Cost
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
